import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

export interface IRecordTree {
  /** Every known record, not only the tree's. */
  recordById: ReadonlyMap<string, IResourceRecord>
  rootIds: readonly string[]
  childIdsById: ReadonlyMap<string, readonly string[]>
  offeredIds: ReadonlySet<string>
  getParentId: (id: string) => string | null
  /** Root first, without the record itself. */
  getAncestorIds: (id: string) => readonly string[]
  /** The record, when offered, and every offered record under it. */
  getOfferedSubtree: (id: string) => readonly IResourceRecord[]
}

/**
 * The offered records as one tree along their `parent`. An ancestor that is not offered
 * still gets a node, so a record keeps its place; one `isPickable` passes (a search or a
 * filter hid it) counts as offered. Siblings keep the offered order; an ancestor that is
 * not offered sorts by the first record offered under it. A parent missing from
 * `allRecords` makes its child a root, and a parent cycle is cut where it closes.
 */
export function buildRecordTree(
  offered: readonly IResourceRecord[],
  allRecords: readonly IResourceRecord[],
  isPickable?: (id: string) => boolean,
): IRecordTree {
  const recordById = new Map<string, IResourceRecord>()
  allRecords.forEach(record => {
    if (!record.isDeleted) recordById.set(record.id, record)
  })

  const rawParentOf = (id: string): string | null => {
    const parentId = recordById.get(id)?.parent?.parentId
    return parentId && recordById.has(parentId) ? parentId : null
  }

  const parentById = new Map<string, string | null>()
  const getParentId = (id: string): string | null => {
    const cached = parentById.get(id)
    if (cached !== undefined) return cached

    let parentId = rawParentOf(id)
    const seen = new Set<string>()
    for (let cursor = parentId; cursor && !seen.has(cursor);) {
      if (cursor === id) {
        parentId = null
        break
      }
      seen.add(cursor)
      cursor = rawParentOf(cursor)
    }
    parentById.set(id, parentId)
    return parentId
  }

  const offeredIds = new Set(offered.map(record => record.id))
  const rankById = new Map<string, number>()
  offered.forEach((record, index) => rankById.set(record.id, index))
  // In offered order, so the first record to reach an ancestor is the lowest rank under it.
  offered.forEach((record, index) => {
    for (let id = getParentId(record.id); id; id = getParentId(id)) {
      if (offeredIds.has(id)) continue
      if (rankById.has(id)) break
      rankById.set(id, index)
    }
  })

  rankById.forEach((_, id) => {
    if (!offeredIds.has(id) && isPickable?.(id)) offeredIds.add(id)
  })

  const rootIds: string[] = []
  const childIdsById = new Map<string, string[]>()
  rankById.forEach((_, id) => {
    const parentId = getParentId(id)
    if (!parentId) {
      rootIds.push(id)
      return
    }
    const siblings = childIdsById.get(parentId)
    if (siblings) siblings.push(id)
    else childIdsById.set(parentId, [id])
  })
  const byRank = (a: string, b: string) => rankById.get(a)! - rankById.get(b)!
  rootIds.sort(byRank)
  childIdsById.forEach(ids => ids.sort(byRank))

  const ancestorsById = new Map<string, readonly string[]>()
  const getAncestorIds = (id: string): readonly string[] => {
    const cached = ancestorsById.get(id)
    if (cached) return cached
    const parentId = getParentId(id)
    const ancestors = parentId ? [...getAncestorIds(parentId), parentId] : []
    ancestorsById.set(id, ancestors)
    return ancestors
  }

  const subtreeById = new Map<string, readonly IResourceRecord[]>()
  const getOfferedSubtree = (id: string): readonly IResourceRecord[] => {
    const cached = subtreeById.get(id)
    if (cached) return cached
    const record = recordById.get(id)
    const subtree = [
      ...(record && offeredIds.has(id) ? [record] : []),
      ...(childIdsById.get(id) ?? []).flatMap(getOfferedSubtree),
    ]
    subtreeById.set(id, subtree)
    return subtree
  }

  return {
    recordById,
    rootIds,
    childIdsById,
    offeredIds,
    getParentId,
    getAncestorIds,
    getOfferedSubtree,
  }
}
